import {
  Grid,
  TextField,
  Typography,
  makeStyles,
  useTheme,
} from "@material-ui/core";
import { KeyboardTimePicker } from "@material-ui/pickers";
import React, { useEffect, useState } from "react";
import DaySelect from "./DaySelect";
import DeleteIcon from "@material-ui/icons/Delete";
import moment from "moment";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";

import {
  constructMinuteOfWeekArray,
  getStartMinuteOfWeekByDay,
} from "../../../utils/time-conversions";
import _ from "lodash";
import ButtonCommon from "../../buttons/ButtonCommon";
import { CustomTheme } from "../../../types/customTheme";
import { Autocomplete } from "@material-ui/lab";

export interface OpeningTimeSlotProps {
  openingTimesSelectionList: any;
  openingTimeSelection: any;
  setOpeningTimesSelectionList: Function;
  isNeedDropDown?: any;
  autocompleteData?: any;
}

const useStyles = makeStyles((theme: CustomTheme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    borderRadius: 10,
    border: `1px solid ${theme.palette.background.entity_border}`,
    padding: 16,
    margin: 4,
  },
  titleColor: {
    color: theme.palette.custom.orange.contrastText,
  },
  deleteButtonStyle: {
    display: "flex",
    justifyContent: "start",
    alignItems: "center",
    [theme.breakpoints.up("sm")]: {
      justifyContent: "center",
    },
    [theme.breakpoints.down("xs")]: {
      justifyContent: "end",
    },
  },
  firstDatePickerStyle: {
    marginLeft: "8px",
    paddingRight: "8px",
    "& .MuiPickersClockPointer-pointer": {
      backgroundColor: `${theme.palette.background.default} !important`,
    },
    "& .MuiPickersClockNumber-clockNumberSelected": {
      backgroundColor: `${theme.palette.background.default} !important`,
    },
    // "& .MuiPickersClock-container": {
    //   backgroundColor: "red !important",
    // },
    [theme.breakpoints.down("sm")]: {
      marginLeft: "0px",
    },
    [theme.breakpoints.down("xs")]: {
      paddingRight: "0px",
    },
    [`& fieldset`]: {
      borderRadius: "10px",
      border: `1px solid ${theme.palette.background.entity_border}`,
      cursor: "pointer",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${theme.palette.background.entity_border}`,
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${theme.palette.background.entity_border}`,
    },
  },
  secondDatePickerStyle: {
    paddingLeft: "8px",
    [theme.breakpoints.down("xs")]: {
      paddingLeft: "0px",
    },
    "& .MuiPickersClockPointer-pointer": {
      backgroundColor: `${theme.palette.background.default} !important`,
    },
    "& .MuiPickersClockNumber-clockNumberSelected": {
      backgroundColor: `${theme.palette.background.default} !important`,
    },
    [`& fieldset`]: {
      borderRadius: "10px",
      border: `1px solid ${theme.palette.background.entity_border}`,
      cursor: "pointer",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${theme.palette.background.entity_border}`,
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${theme.palette.background.entity_border}`,
    },
  },
  autoComplete: {
    marginTop: "4px",
    "& .MuiAutocomplete-input": {
      color: theme.palette.custom.orange.contrastText,
    },
    "& .MuiInputBase-root": {
      color: "inherit",
      backgroundColor: theme.palette.background.entity_highlight_background,
      borderRadius: 10,
    },
    [`& fieldset`]: {
      borderRadius: 10,
      border: "none",
      color: theme.palette.custom.orange.contrastText,
    },
  },
  datePickerStyle: {
    display: "flex",
    justifyContent: "space-between",
  },
}));

/* This component show the opening time selection modal.
   It shows the data coming from the backend and processes the updated and inserted data to be sent to the backend. */
const OpeningTimeSlot: React.FunctionComponent<OpeningTimeSlotProps> = ({
  openingTimesSelectionList,
  openingTimeSelection,
  setOpeningTimesSelectionList,
  isNeedDropDown,
  autocompleteData,
}) => {
  const classes = useStyles();

  const [openTime, setOpenTime] = useState<moment.Moment>(moment());
  const [closeTime, setCloseTime] = useState<moment.Moment>(moment());
  const [selectedDays, setSelectedDays] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<any>(null);

  /* The state of openTime, closeTime, and selectedDays is changed 
  when the initial loading or openingTimeSelection changes. */
  useEffect(() => {
    if (
      !_.isEmpty(openingTimeSelection) &&
      !_.isEmpty(openingTimeSelection.nodeList)
    ) {
      const selectedDays = openingTimeSelection.nodeList.map((node: any) =>
        getStartMinuteOfWeekByDay(node.day),
      );
      setSelectedDays(selectedDays);
      setOpenTime(moment(openingTimeSelection.openTime, "HH:mm"));
      setCloseTime(moment(openingTimeSelection.closeTime, "HH:mm"));

      if (autocompleteData) {
        const filter = autocompleteData.filter(
          (item: any) =>
            item.id === openingTimeSelection?.nodeList?.[0]?.alertConfig,
        );
        setSelectedAlert(filter?.[0]);
      }
    }

    if (
      !_.isEmpty(openingTimeSelection) &&
      _.isEmpty(openingTimeSelection.nodeList)
    ) {
      setOpenTime(moment(openingTimeSelection.openTime, "HH:mm"));
      setCloseTime(moment(openingTimeSelection.closeTime, "HH:mm"));
    }
  }, [openingTimeSelection, autocompleteData]);


  /* When the minuteOfWeekArray, openTime, CloseTime are entered, 
  add the openTime, CloseTime to a list and insert the 
  {closeTime: "", day: "", durationMins: minuteOfWeek: openTime: ""} into a nodeList in the same list. */
  const updateOpeningTimeSelectionList = (
    selectedDays: any,
    openTime: any,
    closeTime: any,
  ) => {
    const mowArray = constructMinuteOfWeekArray(
      selectedDays,
      openTime,
      closeTime,
    );
    let updatedOpeningTimesSelectionList = _.cloneDeep(
      openingTimesSelectionList,
    );
    /* Get the index of changed opening time. */
    const nodeIdx = updatedOpeningTimesSelectionList.findIndex(
      (node: any) => node.id === openingTimeSelection.id,
    );
    if (selectedAlert) {
      mowArray.map((node: any) => (node.alertConfig = selectedAlert.id));
    }
    // Updating 'opening time details' to send to the backend.
    updatedOpeningTimesSelectionList[nodeIdx].nodeList = mowArray;
    updatedOpeningTimesSelectionList[nodeIdx].openTime =
      openTime.format("HH:mm");
    updatedOpeningTimesSelectionList[nodeIdx].closeTime =
      closeTime.format("HH:mm");

    setOpeningTimesSelectionList(updatedOpeningTimesSelectionList);
  };

  /* Handle close time selection modal */
  const handleOpenTime = async (time: Date | null) => {
    await setOpenTime(moment(time));
    updateOpeningTimeSelectionList(selectedDays, moment(time), closeTime);
  };

  /* Handle close time selection modal */
  const handleCloseTime = async (time: Date | null) => {
    await setCloseTime(moment(time));
    updateOpeningTimeSelectionList(selectedDays, openTime, moment(time));
  };

  /* Handle date selection */
  const handleSelectDays = async (e: any, selections: any) => {
    await setSelectedDays(selections);
    updateOpeningTimeSelectionList(selections, openTime, closeTime);
  };

  /* Remove time slot */
  const handleDelete = () => {
    const filtered = _.cloneDeep(openingTimesSelectionList).filter(
      (node: any) => node.id !== openingTimeSelection.id,
    );
    setOpeningTimesSelectionList(filtered);
  };

  const handleChangeMasterLocation = (e: any, masterLocationData: any) => {
    let updatedOpeningTimesSelectionList = _.cloneDeep(
      openingTimesSelectionList,
    );

    /* Get the index of changed opening time. */
    const nodeIdx = updatedOpeningTimesSelectionList.findIndex(
      (node: any) => node.id === openingTimeSelection.id,
    );

    // Updating 'opening time details' to send to the backend.
    if (nodeIdx !== -1) {
      updatedOpeningTimesSelectionList[nodeIdx].nodeList.map((node: any) => {
        node.alertConfig = masterLocationData.id;
      });
      // updatedOpeningTimesSelectionList[nodeIdx].nodeList.alertConfig = masterLocationData.id;
      setOpeningTimesSelectionList(updatedOpeningTimesSelectionList);
    }
    setSelectedAlert(masterLocationData);
  };

  const theme: CustomTheme = useTheme();

  return (
    <div className={classes.root}>
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          md={5}
          lg={4}
          style={{ display: "flex", alignItems: "center", marginTop: "8px" }}
        >
          <DaySelect
            handleSelectDays={handleSelectDays}
            selectedDays={selectedDays}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={10}
          md={6}
          lg={7}
          className={classes.datePickerStyle}
        >
          <Grid container style={{ placeItems: "center" }}>
            <Grid item xs={12} sm={isNeedDropDown ? 4 : 6}>
              <KeyboardTimePicker
                margin="normal"
                ampm={false}
                id="time-picker"
                label="From time"
                value={openTime}
                onChange={handleOpenTime}
                inputVariant={"outlined"}
                className={classes.firstDatePickerStyle}
                fullWidth
                DialogProps={{ className: classes.firstDatePickerStyle }}
                okLabel={<Typography color="secondary">Ok</Typography>}
                cancelLabel={<Typography color="secondary">Cancel</Typography>}
                InputProps={{
                  style: {
                    borderRadius: 10,
                    border: `1px solid ${theme.palette.background.entity_border}`,
                    height: 56,
                    backgroundColor:
                      theme.palette.background.entity_highlight_background,
                  },
                }}
                KeyboardButtonProps={{
                  "aria-label": "change time",
                }}
              />
            </Grid>
            <Grid item xs={12} sm={isNeedDropDown ? 4 : 6}>
              <KeyboardTimePicker
                margin="normal"
                ampm={false}
                id="time-picker"
                label="To time"
                value={closeTime}
                onChange={handleCloseTime}
                inputVariant={"outlined"}
                className={classes.secondDatePickerStyle}
                DialogProps={{ className: classes.secondDatePickerStyle }}
                fullWidth
                okLabel={<Typography color="secondary">Ok</Typography>}
                cancelLabel={<Typography color="secondary">Cancel</Typography>}
                InputProps={{
                  style: {
                    borderRadius: 10,
                    border: `1px solid ${theme.palette.background.entity_border}`,
                    height: 56,
                    backgroundColor:
                      theme.palette.background.entity_highlight_background,
                  },
                }}
                KeyboardButtonProps={{
                  "aria-label": "change time",
                }}
              />
            </Grid>
            {isNeedDropDown && (
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  open={open}
                  onOpen={() => setOpen(true)}
                  onClose={() => setOpen(false)}
                  size={"medium"}
                  id="locationSelectGlobal"
                  color="inherit"
                  options={autocompleteData}
                  value={selectedAlert}
                  getOptionLabel={(option: any) => option.label || ""}
                  disableClearable
                  disabled={false}
                  style={{ marginLeft: "8px" }}
                  onChange={handleChangeMasterLocation}
                  classes={{ root: classes.autoComplete }}
                  popupIcon={<ArrowDropDownIcon color={"inherit"} />}
                  renderOption={(props: any) => {
                    return (
                      <Grid
                        container
                        {...props}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          zIndex: 0,
                          height: "20px",
                          marginBottom: "4px",
                        }}
                      >
                        <Grid item xs={12}>
                          <Typography
                            variant="body2"
                            className={classes.titleColor}
                          >
                            {props.label}
                          </Typography>
                        </Grid>
                      </Grid>
                    );
                  }}
                  renderInput={(params: any) => (
                    <TextField
                      color="inherit"
                      {...params}
                      label=""
                      placeholder="Select Alert Configuration"
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} sm={2} md={1} className={classes.deleteButtonStyle}>
          <ButtonCommon
            style={{ height: "40px", marginTop: "2px" }}
            color="red"
            onClick={handleDelete}
          >
            <DeleteIcon />
          </ButtonCommon>
        </Grid>
      </Grid>
    </div>
  );
};

export default OpeningTimeSlot;
